import { useCallback, useEffect, useState } from "react";

import { ReactComponent as AiIcon } from "../../../assets/icons/ai.svg";
import { aiService } from "../../../services/ai.service";
import { getAiErrorMessage } from "../../../utils/aiErrorMessage";

import styles from "./CommunicationDetailPanel.module.css";

const formatUpdatedAt = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ConversationSummaryPanel = ({ leadId, templateGroupId, subjectGroupId }) => {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const hasTemplateGroup = Boolean(templateGroupId);

  const buildRequestPayload = useCallback(
    () => ({
      lead_id: leadId,
      template_group_id: templateGroupId,
      ...(subjectGroupId ? { subject_group_id: subjectGroupId } : {}),
    }),
    [leadId, templateGroupId, subjectGroupId],
  );

  useEffect(() => {
    if (!leadId || !templateGroupId) {
      setSummary(null);
      setErrorMessage(null);
      return undefined;
    }

    let cancelled = false;

    const loadStoredSummary = async () => {
      setLoadingSummary(true);
      setErrorMessage(null);

      try {
        const data = await aiService.getConversationSummary(buildRequestPayload());
        if (!cancelled) setSummary(data);
      } catch (error) {
        if (cancelled) return;

        if (error?.response?.status === 404) {
          setSummary(null);
        } else {
          setSummary(null);
          setErrorMessage(getAiErrorMessage(error));
        }
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };

    loadStoredSummary();

    return () => {
      cancelled = true;
    };
  }, [leadId, templateGroupId, buildRequestPayload]);

  const handleGenerate = async () => {
    if (!hasTemplateGroup || generating) return;

    // Reveal the panel so the user sees the result they just asked for.
    setIsOpen(true);
    setGenerating(true);
    setErrorMessage(null);

    try {
      const data = await aiService.refreshConversationSummary(buildRequestPayload());
      setSummary(data);
    } catch (error) {
      setErrorMessage(
        getAiErrorMessage(error, {
          notFoundMessage: "No hay respuestas nuevas para resumir.",
        }),
      );
    } finally {
      setGenerating(false);
    }
  };

  const summaryText = summary?.summary_current;
  const buttonLabel = generating
    ? "Generando..."
    : summaryText
      ? "Actualizar"
      : "Generar resumen";

  return (
    <div className={styles.summaryPanel}>
      <div className={styles.summaryHeader}>
        <button
          type="button"
          className={styles.summaryToggle}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
        >
          <span className={styles.summaryTitle}>
            <AiIcon className={styles.summaryTitleIcon} />
            Resumen IA
          </span>
        </button>

        <button
          type="button"
          className={styles.aiButton}
          onClick={handleGenerate}
          disabled={!hasTemplateGroup || generating || loadingSummary}
        >
          {generating ? (
            <span className={styles.aiButtonSpinner} aria-hidden="true" />
          ) : (
            <AiIcon className={styles.aiButtonIcon} />
          )}
          {buttonLabel}
        </button>
      </div>

      {isOpen && (
        <div className={styles.summaryBody}>
          {!hasTemplateGroup ? (
            <p className={styles.summaryEmpty}>
              Resumen no disponible para esta comunicación todavía.
            </p>
          ) : loadingSummary ? (
            <p className={styles.summaryEmpty}>Cargando resumen...</p>
          ) : summaryText ? (
            <>
              <p className={styles.summaryContent}>{summaryText}</p>
              {summary?.updated && (
                <span className={styles.summaryUpdatedAt}>
                  Actualizado {formatUpdatedAt(summary.updated)}
                </span>
              )}
            </>
          ) : (
            <p className={styles.summaryEmpty}>
              Aún no hay un resumen para esta conversación.
            </p>
          )}

          {errorMessage && <p className={styles.aiError}>{errorMessage}</p>}
        </div>
      )}
    </div>
  );
};

export default ConversationSummaryPanel;
